// Does a `pending` task's hold mean NO MACHINE will place it? (#6231) Split from `./hold.ts`,
// which is near the 800-line budget, and because this is one reader's classification of the
// vocabulary, not part of what a hold IS.
//
// THE READER. A babysit fix round that sits `pending` past the CI ceiling holds its pull
// request: one follow-up is in flight per PR, so every later mint dedupes behind it
// (`apps/worker` `babysit/followup-executors.ts`). When a machine will never take the round,
// that wait is forever and a person must be told (`followup_unplaceable`, which mails). When
// the hold has its own owner or its own clock, the wait stays the silent `followup_held` —
// the hold's owner already hears about it, or the hold lapses by itself.
//
// TOTAL over `TaskHoldReason`, so a new member does not compile until someone decides which
// class it lands in. A NULL hold is `no_machine` too: no dispatcher pass even said why the row
// is waiting, which is what a pending row on an account with no box that will take it looks
// like (`pr_babysit` declares no `requires`, so `no_capable_box` never stamps it — #6068 and
// #6205 sat queued that way with nobody told).

import { TaskHoldReason } from './hold';

/** The two classes; see the module header. */
export type TaskHoldMachineClass = 'no_machine' | 'held';

/**
 * Every hold, classified. `no_machine`: no online box can serve the row — a missing
 * capability, a missing `service:*` token, no box that holds a mailbox, every online machine
 * has the task's role switched off (`no_role_machine`: the machines refuse the work, so no
 * machine will place the round, and no other notice tells a person about this hold, so the
 * `followup_unplaceable` mail is not a duplicate). `held`: everything
 * else, each with its owner or clock named:
 *
 *   key-outage holds — exactly `KEY_OUTAGE_TASK_HOLD_REASONS` (`no_usable_keys`,
 *     `credential_cooling`, `review_awaiting_model`, `provider_key_refused`,
 *     `no_screen_key`; `entities/key-outage.ts`): the key-outage notice, and the
 *     key's own wake;
 *   `selection_waiting` — deliberately NOT in that membership
 *     (`entities/key-outage.ts`: the lane's own SELECTED key waits while the rest
 *     of the pool may be healthy and idle, so "none of your keys can serve the
 *     work" would be false). Wakes: a route change (`releaseHoldsOnRouteChange`),
 *     a key turning `valid` (`releaseHoldsOnKeyValid`), or the clock; the review
 *     hold-notice reword (`apps/worker` `review/check-hold-notice.ts`) re-words
 *     the PR's queued check;
 *   `no_screen_key` — the run-start screen's own wait: `releaseHoldsOnKeyValid` or
 *     the `NO_SCREEN_KEY_RECHECK_MS` clock; a member of the outage list above, so
 *     the key-outage banner names it too (`KeyOutageNotice.tsx` renders
 *     `key_outage.reason_no_screen_key`, in both locales);
 *   provider and pool walls (`pool_cooling`, `provider_capacity`, `key_saturated`) — a clock;
 *   `key_reserved` — a free slot held for higher-priority work: a clock, or a route change,
 *     exactly as `key_saturated` (#6711);
 *   `seat_capped` — the seat-cap notice, once per window, and a slot freeing;
 *   `repo_not_onboarded` — the onboarding wait told to the maintainer;
 *   `area_busy`, `upstream_group_unmerged`, `resume_backoff`, `pr_quiet_window` —
 *     other work, or a clock;
 *   `box_fault_excluded` — a clock for its box-fault exclusion. Its decode hold (#6237:
 *     held for a newer runner release) has neither on a fleet that never updates, and
 *     stays `held` all the same: a box on a newer release DOES place it, so "no machine
 *     will ever take it" is not known, and mailing `followup_unplaceable` for a round an
 *     update starts would be the escalation that hold exists to avoid. Its owner is the
 *     stalled-queue alert (`apps/worker` `cron/stalled-queue.ts`), which fires once the
 *     task has been pending 24 hours counted from its `created_at`, not from the hold.
 *     That alert is the only escalation it gets. Its memory hold (#7081: held for a box
 *     with a larger per-task cap) is `held` for the same reason — a resized or newly
 *     connected box places it — and `task stalls` names the cap it needs;
 *   `error_issue_pending` — the error-issue lane filing the public issue, bounded by a clock;
 *   `ci_pending` — the head's CI completing, bounded by a clock;
 *   `plan_paused` — a person's pause of the epic, released by Resume (#6748);
 *   `machine_pinned` — the task's own machine, on its next pass, or the pin cleared when that
 *     machine goes offline, is drained or is revoked (#6748);
 *   `mode_disabled` — an operator's own switch;
 *   `repo_env_fault`, `env_fault_exhausted` — the repo-env alert, and a blocker.
 */
export const TASK_HOLD_MACHINE: Readonly<Record<TaskHoldReason, TaskHoldMachineClass>> = {
  [TaskHoldReason.NoCapableBox]: 'no_machine',
  [TaskHoldReason.NoServiceCapacity]: 'no_machine',
  [TaskHoldReason.NoConverseCapacity]: 'no_machine',
  [TaskHoldReason.NoRoleMachine]: 'no_machine',
  [TaskHoldReason.NoUsableKeys]: 'held',
  [TaskHoldReason.CredentialCooling]: 'held',
  [TaskHoldReason.ReviewAwaitingModel]: 'held',
  [TaskHoldReason.ProviderKeyRefused]: 'held',
  [TaskHoldReason.SelectionWaiting]: 'held',
  [TaskHoldReason.NoScreenKey]: 'held',
  [TaskHoldReason.ErrorIssuePending]: 'held',
  [TaskHoldReason.PoolCooling]: 'held',
  [TaskHoldReason.ProviderCapacity]: 'held',
  [TaskHoldReason.KeySaturated]: 'held',
  [TaskHoldReason.KeyReserved]: 'held',
  [TaskHoldReason.SeatCapped]: 'held',
  [TaskHoldReason.RepoNotOnboarded]: 'held',
  [TaskHoldReason.AreaBusy]: 'held',
  [TaskHoldReason.UpstreamGroupUnmerged]: 'held',
  [TaskHoldReason.UpstreamLegUndeployed]: 'held',
  [TaskHoldReason.ResumeBackoff]: 'held',
  [TaskHoldReason.PrQuietWindow]: 'held',
  [TaskHoldReason.CiPending]: 'held',
  [TaskHoldReason.BoxFaultExcluded]: 'held',
  [TaskHoldReason.ModeDisabled]: 'held',
  [TaskHoldReason.RepoEnvFault]: 'held',
  [TaskHoldReason.EnvFaultExhausted]: 'held',
  [TaskHoldReason.PlanPaused]: 'held',
  [TaskHoldReason.MachinePinned]: 'held',
};

/**
 * Does a `pending` row carrying `holdReason` (`null` = none stamped) wait on a machine that
 * will not come? See {@link TASK_HOLD_MACHINE}. Says nothing about a row in any other status:
 * the caller checks it is pending.
 */
export function holdMeansNoMachine(holdReason: TaskHoldReason | null): boolean {
  return holdReason === null || TASK_HOLD_MACHINE[holdReason] === 'no_machine';
}

/** The two refile classes; see {@link TASK_HOLD_REFILE}. */
export type TaskHoldRefile = 'fresh_row_clears' | 'fresh_row_inherits';

/**
 * Does a HELD row's hold follow it onto the fresh row a rescue refiles it as (#6343)?
 * `supersedeAndMintBabysitRound` (`packages/db/src/queries/tasks/babysit-supersede.ts`)
 * retires a `pending` held row and mints its successor in one transaction; this table is
 * what the fresh row's hold state answers to.
 *
 *   fresh_row_inherits — the hold is a fact about the ACCOUNT, the REPO, the KEY or the
 *     account's MACHINES (`no_role_machine`), not about the row: a seat cap, a repo env fault, a saturated or reserved key are just as true of the
 *     fresh row, and minting it unheld would send the claim pass after a row that cannot
 *     run. The dispatcher pass that derived the hold re-stamps the fresh row on its next
 *     tick, the same way it stamped the retired one.
 *   fresh_row_clears — the hold is the row's OWN spent budget: `env_fault_exhausted` and
 *     `resume_backoff` count THIS row's attempts. The refile is the deliberate reset — it is
 *     why the round is refiled at all — so the fresh row starts clean.
 *
 * TOTAL over `TaskHoldReason`, like {@link TASK_HOLD_MACHINE} above: a hold added without a
 * refile class does not compile.
 */
export const TASK_HOLD_REFILE = {
  [TaskHoldReason.NoCapableBox]: 'fresh_row_inherits',
  [TaskHoldReason.NoServiceCapacity]: 'fresh_row_inherits',
  [TaskHoldReason.NoConverseCapacity]: 'fresh_row_inherits',
  [TaskHoldReason.NoUsableKeys]: 'fresh_row_inherits',
  [TaskHoldReason.CredentialCooling]: 'fresh_row_inherits',
  [TaskHoldReason.ReviewAwaitingModel]: 'fresh_row_inherits',
  [TaskHoldReason.ProviderKeyRefused]: 'fresh_row_inherits',
  [TaskHoldReason.SelectionWaiting]: 'fresh_row_inherits',
  [TaskHoldReason.NoScreenKey]: 'fresh_row_inherits',
  [TaskHoldReason.ErrorIssuePending]: 'fresh_row_inherits',
  [TaskHoldReason.PoolCooling]: 'fresh_row_inherits',
  [TaskHoldReason.ProviderCapacity]: 'fresh_row_inherits',
  [TaskHoldReason.KeySaturated]: 'fresh_row_inherits',
  [TaskHoldReason.KeyReserved]: 'fresh_row_inherits',
  [TaskHoldReason.SeatCapped]: 'fresh_row_inherits',
  [TaskHoldReason.RepoNotOnboarded]: 'fresh_row_inherits',
  [TaskHoldReason.AreaBusy]: 'fresh_row_inherits',
  [TaskHoldReason.UpstreamGroupUnmerged]: 'fresh_row_inherits',
  [TaskHoldReason.UpstreamLegUndeployed]: 'fresh_row_inherits',
  [TaskHoldReason.ResumeBackoff]: 'fresh_row_clears',
  [TaskHoldReason.PrQuietWindow]: 'fresh_row_inherits',
  [TaskHoldReason.CiPending]: 'fresh_row_inherits',
  [TaskHoldReason.BoxFaultExcluded]: 'fresh_row_inherits',
  [TaskHoldReason.ModeDisabled]: 'fresh_row_inherits',
  [TaskHoldReason.NoRoleMachine]: 'fresh_row_inherits',
  [TaskHoldReason.RepoEnvFault]: 'fresh_row_inherits',
  [TaskHoldReason.EnvFaultExhausted]: 'fresh_row_clears',
  // #6748: both re-derived by the next tick — a fresh row on a paused epic re-stamps, and a
  // fresh row carries no pin, so the machine-pinned pass retracts what it inherited.
  [TaskHoldReason.PlanPaused]: 'fresh_row_inherits',
  [TaskHoldReason.MachinePinned]: 'fresh_row_inherits',
} as const satisfies Record<TaskHoldReason, TaskHoldRefile>;
